import React, { useState, useRef } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Title,
  Paper,
  Flex,
  Alert,
  Badge,
  FileInput,
  Table,
  ScrollArea,
  LoadingOverlay,
  Divider
} from '@mantine/core';
import {
  IconInfoCircle,
  IconDownload,
  IconFileImport,
  IconCheck,
  IconX,
  IconAlertTriangle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import * as XLSX from 'xlsx';

export interface ImportedQuestion {
  id: string;
  title: string;
  category?: string;
  skalaCategory?: string;
  description?: string;
  type: 'Choose One' | 'Range' | 'Free-text';
  moduleId: string;
  moduleName?: string;
  respondentType: string;
  rangeItems?: { label: string; value: string }[];
  isValid: boolean;
  errors: string[];
}

/* eslint-disable */
interface ImportQuestionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (questions: ImportedQuestion[]) => void;
  moduleId: string;
  moduleName: string;
  respondentTypes: string[];
  externalTypeIds?: string[];
  externalTypeNames?: string[];
}

const ImportQuestionModal: React.FC<ImportQuestionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  moduleId,
  moduleName,
  respondentTypes,
  externalTypeNames = []
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importedQuestions, setImportedQuestions] = useState<ImportedQuestion[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Template generation
  const generateTemplate = () => {
    const templateData = [
      {
        Question: 'How would you rate your overall satisfaction with the project outcome?',
        Description: 'Please provide your assessment based on the delivered results',
        Type: 'Range', // Choose One, Range, Free-text
        Category: 'Satisfaction, Project Management',
        SkalaCategory: '1-5',
        RangeItems: 'Excellent:5,Good:4,Average:3,Poor:2,Very Poor:1'
      },
      {
        Question: 'What challenges did you face during the project implementation?',
        Description: 'Describe any obstacles encountered',
        Type: 'Free-text',
        Category: 'Challenges, Implementation',
        SkalaCategory: '',
        RangeItems: ''
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions Template');

    // Set column widths
    const colWidths = [
      { wch: 50 }, // Question
      { wch: 30 }, // Description
      { wch: 15 }, // Type
      { wch: 25 }, // Category
      { wch: 15 }, // SkalaCategory
      { wch: 40 } // RangeItems
    ];
    worksheet['!cols'] = colWidths;

    const fileName = `questions_template_${moduleName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    notifications.show({
      title: 'Template Downloaded',
      message: `Template file "${fileName}" has been downloaded`,
      color: 'green',
      icon: <IconCheck />
    });
  };

  // Validate question data
  const validateQuestion = (
    questionData: any,
    index: number
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!questionData.Question || questionData.Question.trim() === '') {
      errors.push('Question text is required');
    }

    if (!questionData.Type || !['Choose One', 'Range', 'Free-text'].includes(questionData.Type)) {
      errors.push('Type must be one of: Choose One, Range, Free-text');
    }

    if (
      questionData.Type === 'Range' &&
      (!questionData.RangeItems || questionData.RangeItems.trim() === '')
    ) {
      errors.push('Range items are required for Range type questions');
    }

    if (
      questionData.Type === 'Choose One' &&
      (!questionData.RangeItems || questionData.RangeItems.trim() === '')
    ) {
      errors.push('Options are required for Choose One type questions');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Parse range items from string format
  const parseRangeItems = (rangeItemsStr: string): { label: string; value: string }[] => {
    if (!rangeItemsStr || rangeItemsStr.trim() === '') return [];

    try {
      return rangeItemsStr
        .split(',')
        .map((item) => {
          const [label, value] = item.split(':');
          return {
            label: label?.trim() || '',
            value: value?.trim() || label?.trim() || ''
          };
        })
        .filter((item) => item.label !== '');
    } catch (error) {
      return [];
    }
  };

  // Process uploaded file
  const processFile = async (uploadedFile: File) => {
    if (!uploadedFile) return;

    setIsProcessing(true);
    try {
      const arrayBuffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      setPreviewData(jsonData);

      // Process and validate questions
      const processedQuestions: ImportedQuestion[] = jsonData.map((row: any, index: number) => {
        const validation = validateQuestion(row, index);
        const respondentType = respondentTypes[0]; // Use first respondent type

        return {
          id: `imported_${Date.now()}_${index}`,
          title: row.Question || '',
          description: row.Description || '',
          category: row.Category || '',
          skalaCategory: row.SkalaCategory || '',
          type: row.Type || 'Free-text',
          moduleId: moduleId,
          moduleName: moduleName,
          respondentType: respondentType,
          rangeItems: parseRangeItems(row.RangeItems || ''),
          isValid: validation.isValid,
          errors: validation.errors
        };
      });

      setImportedQuestions(processedQuestions);

      const validCount = processedQuestions.filter((q) => q.isValid).length;
      const invalidCount = processedQuestions.length - validCount;

      if (invalidCount > 0) {
        notifications.show({
          title: 'Import Validation',
          message: `${validCount} valid questions, ${invalidCount} with errors. Please review before importing.`,
          color: 'yellow',
          icon: <IconAlertTriangle />
        });
      } else {
        notifications.show({
          title: 'Import Success',
          message: `${validCount} questions imported successfully`,
          color: 'green',
          icon: <IconCheck />
        });
      }
    } catch (error) {
      console.error('Error processing file:', error);
      notifications.show({
        title: 'Import Error',
        message: 'Failed to process the uploaded file. Please check the file format.',
        color: 'red',
        icon: <IconX />
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = (uploadedFile: File | null) => {
    setFile(uploadedFile);
    if (uploadedFile) {
      processFile(uploadedFile);
    } else {
      setImportedQuestions([]);
      setPreviewData([]);
    }
  };

  const handleSubmit = () => {
    const validQuestions = importedQuestions.filter((q) => q.isValid);
    if (validQuestions.length === 0) {
      notifications.show({
        title: 'No Valid Questions',
        message: 'Please ensure at least one question is valid before importing',
        color: 'red',
        icon: <IconX />
      });
      return;
    }

    onSubmit(validQuestions);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setImportedQuestions([]);
    setPreviewData([]);
    setIsProcessing(false);
    onClose();
  };

  const validQuestionsCount = importedQuestions.filter((q) => q.isValid).length;
  const invalidQuestionsCount = importedQuestions.length - validQuestionsCount;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      size='xl'
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isProcessing} overlayProps={{ radius: 'sm', blur: 2 }} />

        <Stack gap='md' p={16}>
          <Title order={3}>Import Questions from Excel</Title>

          <Alert icon={<IconInfoCircle size={16} />} title='Import Information' color='blue'>
            Importing questions for <b>{respondentTypes.join(', ')}</b> assessment in module{' '}
            <b>{moduleName}</b>
            {respondentTypes.includes('External') && externalTypeNames.length > 0 && (
              <>
                <br />
                <br />
                <Text>External assessment types:</Text>
                <Group mt={5}>
                  {externalTypeNames.map((typeName, idx) => (
                    <Badge key={idx} color='blue' variant='light'>
                      {typeName}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
          </Alert>

          {/* Template Download Section */}
          <Paper p='md' withBorder>
            <Flex justify='space-between' align='center'>
              <div>
                <Text fw={600} size='sm' mb={4}>
                  Download Template
                </Text>
                <Text size='xs' c='dimmed'>
                  Get the Excel template with the correct format and sample data
                </Text>
              </div>
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={generateTemplate}
                variant='outline'
                color='#7B2183'
              >
                Download Template
              </Button>
            </Flex>
          </Paper>

          <Divider label='Upload Your File' labelPosition='center' />

          {/* File Upload Section */}
          <FileInput
            label='Upload Excel File'
            description='Upload an .xlsx or .xls file containing your questions'
            placeholder='Click to select file'
            accept='.xlsx,.xls'
            value={file}
            onChange={handleFileUpload}
            leftSection={<IconFileImport size={16} />}
          />

          {/* Preview and Validation Results */}
          {importedQuestions.length > 0 && (
            <>
              <Group justify='space-between' align='center'>
                <Text fw={600} size='lg'>
                  Import Preview
                </Text>
                <Group gap='xs'>
                  <Badge color='green' variant='light'>
                    {validQuestionsCount} Valid
                  </Badge>
                  {invalidQuestionsCount > 0 && (
                    <Badge color='red' variant='light'>
                      {invalidQuestionsCount} Invalid
                    </Badge>
                  )}
                </Group>
              </Group>

              <ScrollArea h={300}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '50px' }}>Status</Table.Th>
                      <Table.Th>Question</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Category</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {importedQuestions.map((question, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          {question.isValid ? (
                            <IconCheck size={16} color='green' />
                          ) : (
                            <IconX size={16} color='red' />
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size='sm' lineClamp={2}>
                            {question.title}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size='sm' variant='light'>
                            {question.type}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size='xs' c='dimmed'>
                            {question.category || 'No category'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              {invalidQuestionsCount > 0 && (
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  title='Validation Errors'
                  color='yellow'
                >
                  {invalidQuestionsCount} question(s) have validation errors. Only valid questions
                  will be imported. Please fix the errors in your Excel file and re-upload if
                  needed.
                </Alert>
              )}
            </>
          )}

          <Group justify='flex-end' mt='md'>
            <Button variant='outline' color='#7B2183' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color='#7B2183'
              onClick={handleSubmit}
              disabled={validQuestionsCount === 0}
              leftSection={<IconFileImport size={16} />}
            >
              Import {validQuestionsCount} Question{validQuestionsCount !== 1 ? 's' : ''}
            </Button>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
};

export default ImportQuestionModal;
